import React, { useState } from 'react'
import {
	FormControl,
	MenuItem,
	TextField,
	IconButton,
	Grid,
	Typography,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button
} from '@material-ui/core'
import { Add, AddCircleOutline } from '@material-ui/icons'
import axios from 'axios'
import iziToast from 'izitoast'

import useIntl from 'hooks/useIntl'
import TextToolTip from 'components/TextToolTip'
import { useStoreon } from 'storeon/react'

// Interfaces
interface SubSection {
	subSectionId: number
	subSectionName: string
}

interface Section {
	sectionId: number
	sectionName: string
	subSections: SubSection[]
}

interface SectionSelectProps {
	sectionData: Section[]
	selectedSection: string
	setSelectedSection: (value: string) => void
	setSubSectionData: (value: SubSection[]) => void
	isCustomSection: boolean
	setIsCustomSection: (value: boolean) => void
	customSection: string
	setCustomSection: (value: string) => void
	fetchSections: () => void // <-- Add this
}

const Bulksections: React.FC<SectionSelectProps> = ({
	sectionData,
	selectedSection,
	setSelectedSection,
	setSubSectionData,
	isCustomSection,
	customSection,
	setCustomSection,
	fetchSections
}) => {
	const [open, setOpen] = useState(false)
	const [sectionName, setSectionName] = useState('')
	const [description, setDescription] = useState('')
	const intl = useIntl()
	const { token } = useStoreon('token')
	const handleSectionChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const value = event.target.value as string
		const selected = sectionData.find(section => section.sectionId.toString() === value)

		if (selected) {
			setSelectedSection(value)
			setSubSectionData(selected.subSections || [])
		}
	}

	const handleOpen = () => {
		setSectionName('')
		setDescription('')
		setOpen(true)
	}

	const handleClose = () => setOpen(false)

	const handleCreateSection = async () => {
		const userId = localStorage.getItem('userId')

		try {
			const requestBody = {
				name: sectionName,
				description,
				createdBy: userId
			}

			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/createSection`,
				requestBody,
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			if (response) {
				iziToast.success({
					message: response.data?.message || 'Section created successfully!'
				})
				fetchSections()
				// console.log('Section Created:', response.data)
				handleClose()
			}
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to create section!'
			iziToast.error({
				message: errorMessage
			})
			console.error('Error creating section:', error)
		}
	}

	return (
		<>
			<Grid>
				<Typography gutterBottom>{intl.formatMessage({ id: 'Section Name' })}</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<FormControl fullWidth>
						{isCustomSection ? (
							<TextField
								fullWidth
								placeholder="Enter Section Name"
								value={customSection}
								onChange={e => setCustomSection(e.target.value)}
							/>
						) : (
							<TextField
								select
								fullWidth
								value={selectedSection}
								onChange={handleSectionChange}
								SelectProps={{
									MenuProps: {
										getContentAnchorEl: null,
										anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
										transformOrigin: { vertical: 'top', horizontal: 'left' },
										PaperProps: {
											style: { maxHeight: 200, overflowY: 'auto' }
										}
									},
									displayEmpty: true
								}}
							>
								<MenuItem value="" disabled>
									Select a Section
								</MenuItem>
								{sectionData.length > 0 ? (
									sectionData.map(section =>
										section && section.sectionId ? (
											<MenuItem
												key={section.sectionId}
												value={section.sectionId.toString()}
											>
												{section.sectionName}
											</MenuItem>
										) : null
									)
								) : (
									<MenuItem disabled>No Sections Available</MenuItem>
								)}
							</TextField>
						)}
					</FormControl>
					<TextToolTip title="Add Section">
						<Add onClick={handleOpen} sx={{ color: '#7D6EF1', cursor: 'pointer' }} />
					</TextToolTip>
				</Box>
			</Grid>

			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>Add New Section</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography gutterBottom>
								{intl.formatMessage({ id: 'Section Name' })}
							</Typography>
							<TextField
								fullWidth
								placeholder="Enter section name"
								value={sectionName}
								onChange={e => setSectionName(e.target.value)}
							/>
						</Grid>

						<Grid item xs={12} sx={{ mt: 2 }}>
							<Typography gutterBottom>
								{intl.formatMessage({ id: 'Description' })}
							</Typography>
							<TextField
								fullWidth
								multiline
								rows={3}
								value={description}
								onChange={e => setDescription(e.target.value)}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleClose} color="secondary">
						{intl.formatMessage({ id: 'Cancel' })}
					</Button>
					<Button onClick={handleCreateSection} color="primary" variant="contained">
						{intl.formatMessage({ id: 'Create' })}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default Bulksections
